import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Chart,
  LineElement,
  PointElement,
  LineController,
  LinearScale,
  Tooltip,
  Filler,
} from "chart.js";

Chart.register(LineElement, PointElement, LineController, LinearScale, Tooltip, Filler);

const WAN_BASE = process.env.REACT_APP_TUMBLE_BACKEND;
console.log("Using backend:", WAN_BASE);
const VIBRATIONS_PATH = "/vibrations";

const RANGE_OPTIONS = [
  { label: "5 min",  minutes: 5  },
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
  { label: "1 hr",   minutes: 60 },
];

function fmt(isoStr) {
  console.log("Formatting timestamp:", isoStr, "new:", new Date(isoStr).toLocaleTimeString());
  return new Date(isoStr).toLocaleTimeString();
}

function toQueryTs(dt) {
  // "2026-06-14T06:57:10" — drop ms
  return dt.toISOString().slice(0, 19) + 'Z';
}

function MetricCard({ label, value }) {
  return (
    <div style={{
      background: "var(--bg-secondary, #f5f5f5)",
      borderRadius: 8,
      padding: "12px 16px",
      minWidth: 110,
      flex: "1 1 110px",
    }}>
      <p style={{ margin: "0 0 4px", fontSize: 12, color: "#888" }}>{label}</p>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>{value ?? "—"}</p>
    </div>
  );
}

export default function Admin() {
  const chartRef  = useRef(null);
  const chartInst = useRef(null);

  const [data,    setData]    = useState(null);   // raw API response
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [minutes, setMinutes] = useState(15);     // selected range

  // Zoom state: [0, 100] percentage of visible window
  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd,   setZoomEnd]   = useState(100);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (mins) => {
    setLoading(true);
    setError(null);
    try {
      let url = WAN_BASE + VIBRATIONS_PATH;
      if (mins) {
        const end   = new Date();
        const start = new Date(end.getTime() - mins * 60 * 1000);
        url += `?start=${toQueryTs(start)}&end=${toQueryTs(end)}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setZoomStart(0);
      setZoomEnd(100);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(minutes); }, [minutes, fetchData]);

  // ── Build flat point array from segments ─────────────────────────────────
  const allPoints = (() => {
    if (!data?.segments) return [];
    const pts = [];
    const si = (data.sample_interval ?? 0.1) * 1000;
    for (const seg of data.segments) {
      const t0 = new Date(seg.start).getTime();
      seg.values.forEach((v, i) => pts.push({ x: t0 + i * si, y: v }));
    }
    return pts;
  })();

  // ── Chart: create / update ───────────────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current) return;

    const n = allPoints.length;
    const s = Math.floor(zoomStart / 100 * n);
    const e = Math.ceil(zoomEnd   / 100 * n);
    const slice = allPoints.slice(s, e);

    const isDark    = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const lineColor = isDark ? "#5DCAA5" : "#1D9E75";
    const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
    const textColor = isDark ? "#B4B2A9" : "#5F5E5A";

    if (chartInst.current) {
      chartInst.current.data.datasets[0].data = slice;
      if (slice.length) {
        chartInst.current.options.scales.x.min = slice[0].x;
        chartInst.current.options.scales.x.max = slice[slice.length - 1].x;
      }
      chartInst.current.update("none");
      return;
    }

    chartInst.current = new Chart(chartRef.current, {
      type: "line",
      data: {
        datasets: [{
          label: "Vibration",
          data: slice,
          borderColor: lineColor,
          backgroundColor: "transparent",
          borderWidth: 1,
          pointRadius: 0,
          tension: 0,
          parsing: false,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: items => new Date(items[0].parsed.x).toLocaleTimeString(),
              label: item  => `Vibration: ${Math.round(item.parsed.y).toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            min: slice[0]?.x,
            max: slice[slice.length - 1]?.x,
            ticks: {
              color: textColor,
              maxTicksLimit: 6,
              callback: v => new Date(v).toLocaleTimeString(),
            },
            grid: { color: gridColor },
          },
          y: {
            ticks: {
              color: textColor,
              maxTicksLimit: 5,
              callback: v => Math.round(v).toLocaleString(),
            },
            grid: { color: gridColor },
          },
        },
      },
    });

    return () => {
      chartInst.current?.destroy();
      chartInst.current = null;
    };
  }, [allPoints, zoomStart, zoomEnd]);

  // ── Derived metrics ──────────────────────────────────────────────────────
  const allVals = allPoints.map(p => p.y);
  const metrics = allVals.length ? {
    total: allPoints.length.toLocaleString(),
    avg:   Math.round(allVals.reduce((a, b) => a + b, 0) / allVals.length).toLocaleString(),
    min:   Math.min(...allVals).toLocaleString(),
    max:   Math.max(...allVals).toLocaleString(),
    segs:  data?.segments?.length ?? 0,
  } : null;

  const zoomPct = Math.round(zoomEnd - zoomStart);

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "1.5rem 1rem" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <div>
          <Link to="/" style={{ fontSize: 13, color: "#888", textDecoration: "none" }}>
            ← Back
          </Link>
          <h1 style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 500 }}>Vibration monitor</h1>
        </div>
        <button
          onClick={() => fetchData(minutes)}
          disabled={loading}
          style={{ fontSize: 13, padding: "6px 14px" }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#FCEBEB", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: "1rem", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Time range buttons */}
      <div style={{ display: "flex", gap: 6, marginBottom: "1rem", flexWrap: "wrap" }}>
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.minutes}
            onClick={() => setMinutes(opt.minutes)}
            style={{
              fontSize: 12,
              padding: "4px 12px",
              borderRadius: 6,
              border: `0.5px solid ${minutes === opt.minutes ? "#888" : "#ddd"}`,
              background: minutes === opt.minutes ? "#f0f0f0" : "#888",
              cursor: "pointer",
              fontWeight: minutes === opt.minutes ? 500 : 400,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Metric cards */}
      {metrics && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: "1rem" }}>
          <MetricCard label="Samples"  value={metrics.total} />
          <MetricCard label="Average"  value={metrics.avg}   />
          <MetricCard label="Min"      value={metrics.min}   />
          <MetricCard label="Max"      value={metrics.max}   />
          <MetricCard label="Segments" value={metrics.segs}  />
        </div>
      )}

      {/* Gap warning */}
      {data?.gap_count > 0 && (
        <div style={{ fontSize: 12, color: "#BA7517", background: "#FAEEDA", borderRadius: 6, padding: "6px 12px", marginBottom: "1rem" }}>
          ⚠ {data.gap_count} gap{data.gap_count > 1 ? "s" : ""} detected in this time range
        </div>
      )}

      {/* Zoom sliders */}
      {allPoints.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#888" }}>Zoom</span>
          <input
            type="range" min={0} max={100} step={1} value={zoomStart}
            style={{ flex: 1, minWidth: 80 }}
            onChange={e => {
              const v = Math.min(+e.target.value, zoomEnd - 2);
              setZoomStart(v);
            }}
          />
          <input
            type="range" min={0} max={100} step={1} value={zoomEnd}
            style={{ flex: 1, minWidth: 80 }}
            onChange={e => {
              const v = Math.max(+e.target.value, zoomStart + 2);
              setZoomEnd(v);
            }}
          />
          <span style={{ fontSize: 12, color: "#888", minWidth: 80, textAlign: "right" }}>
            {zoomPct}% visible
          </span>
        </div>
      )}

      {/* Chart */}
      <div style={{ position: "relative", width: "100%", height: 280, marginBottom: "1.5rem" }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#888" }}>
            Loading…
          </div>
        )}
        <canvas ref={chartRef} role="img" aria-label="Line chart of vibration sensor values over time" />
      </div>

      {/* Segment list */}
      {data?.segments?.map((seg, i) => {
        const lo  = Math.min(...seg.values);
        const hi  = Math.max(...seg.values);
        const avg = Math.round(seg.values.reduce((a, b) => a + b, 0) / seg.values.length);
        const nextSeg = data.segments[i + 1];
        const gapMs = nextSeg
          ? new Date(nextSeg.start).getTime() - new Date(seg.end).getTime()
          : 0;
        const hasGap = gapMs > (data.sample_interval ?? 0.1) * 1500;

        return (
          <div key={i}>
            <div style={{
              border: "0.5px solid #e0e0e0",
              borderRadius: 8,
              padding: "10px 14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 6,
              fontSize: 13,
              marginBottom: hasGap ? 0 : 8,
            }}>
              <span style={{ color: "#888" }}>Segment {i + 1}</span>
              <span style={{ fontWeight: 500 }}>{fmt(seg.start)} – {fmt(seg.end)}</span>
              <span style={{ color: "#888" }}>
                {seg.sample_count.toLocaleString()} samples &nbsp;·&nbsp;
                avg {avg.toLocaleString()} &nbsp;·&nbsp;
                {lo.toLocaleString()}–{hi.toLocaleString()}
              </span>
            </div>

            {hasGap && (
              <div style={{ padding: "4px 14px 8px", fontSize: 12, color: "#BA7517", display: "flex", alignItems: "center", gap: 6 }}>
                ⚠ Gap: {(gapMs / 1000).toFixed(1)}s of missing data
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
